import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase';
import { Game, gameTemplates, users } from '../types';

const COLLECTION_NAME = 'games';

// Belirli kullanıcının oyunlarını getir
export const getUserGames = async (userId: string): Promise<Game[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      orderBy('name')
    );
    const querySnapshot = await getDocs(q);
    const games: Game[] = [];
    
    querySnapshot.forEach((doc) => {
      const game = { id: doc.id, ...doc.data() } as Game;
      if (game.userId === userId) {
        games.push(game);
      }
    });
    
    return games;
  } catch (error) {
    console.error('Kullanıcı oyunları getirilirken hata:', error);
    return [];
  }
};

// Tüm oyunları getir (admin için)
export const getAllGames = async (): Promise<Game[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('name'));
    const querySnapshot = await getDocs(q);
    const games: Game[] = [];
    
    querySnapshot.forEach((doc) => {
      games.push({ id: doc.id, ...doc.data() } as Game);
    });
    
    return games;
  } catch (error) {
    console.error('Oyunlar getirilirken hata:', error);
    return [];
  }
};

// Tek oyun getir
export const getGame = async (id: string): Promise<Game | null> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Game;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Oyun getirilirken hata:', error);
    return null;
  }
};

// Yeni oyun ekle
export const addGame = async (game: Omit<Game, 'id'>): Promise<string | null> => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...game,
      lastPlayed: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Oyun eklenirken hata:', error);
    return null;
  }
};

// Oyun güncelle
export const updateGame = async (id: string, updates: Partial<Game>): Promise<boolean> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(docRef, {
      ...updates,
      lastPlayed: new Date()
    });
    return true;
  } catch (error) {
    console.error('Oyun güncellenirken hata:', error);
    return false;
  }
};

// Oyun sil
export const deleteGame = async (id: string): Promise<boolean> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Oyun silinirken hata:', error);
    return false;
  }
};

// Kullanıcının tüm oyunlarını sil
export const deleteAllUserGames = async (userId: string): Promise<boolean> => {
  try {
    const userGames = await getUserGames(userId);
    for (const game of userGames) {
      await deleteGame(game.id);
    }
    console.log(`${userId} için tüm oyunlar silindi`);
    return true;
  } catch (error) {
    console.error('Kullanıcı oyunları silinirken hata:', error);
    return false;
  }
};

// Kullanıcının oyunlarını temizle ve yeni oyunları ekle
export const resetUserGames = async (userId: string): Promise<boolean> => {
  try {
    // Önce tüm oyunları sil
    await deleteAllUserGames(userId);
    
    // Sonra yeni oyunları ekle
    for (const gameTemplate of gameTemplates) {
      await addGame({
        ...gameTemplate,
        userId
      });
    }
    console.log(`${userId} için oyunlar başarıyla sıfırlandı`);
    return true;
  } catch (error) {
    console.error('Oyunlar sıfırlanırken hata:', error);
    return false;
  }
};

// Belirli kullanıcı için varsayılan oyunları ekle
export const initializeUserGames = async (userId: string): Promise<void> => {
  try {
    const existingGames = await getUserGames(userId);
    if (existingGames.length === 0) {
      for (const gameTemplate of gameTemplates) {
        await addGame({
          ...gameTemplate,
          userId
        });
      }
      console.log(`${userId} için varsayılan oyunlar başarıyla eklendi`);
    }
  } catch (error) {
    console.error('Varsayılan oyunlar eklenirken hata:', error);
  }
};

// Tüm kullanıcılar için varsayılan oyunları ekle (ilk kurulum için)
export const initializeAllUsersGames = async (): Promise<void> => {
  try {
    for (const user of users) {
      await initializeUserGames(user.id);
    }
    console.log('Tüm kullanıcılar için varsayılan oyunlar eklendi');
  } catch (error) {
    console.error('Tüm kullanıcılar için oyunlar eklenirken hata:', error);
  }
};

// Tüm kullanıcıları yeni oyun listesi ile sıfırla
export const resetAllUsersGames = async (): Promise<void> => {
  try {
    for (const user of users) {
      await resetUserGames(user.id);
    }
    console.log('Tüm kullanıcılar için oyunlar yeni liste ile sıfırlandı');
  } catch (error) {
    console.error('Tüm kullanıcılar için oyunlar sıfırlanırken hata:', error);
  }
};

// Level güncelle
export const updateGameLevel = async (id: string, newLevel: number): Promise<boolean> => {
  return updateGame(id, { currentLevel: newLevel });
};

// Açıklama güncelle
export const updateGameDescription = async (id: string, description: string): Promise<boolean> => {
  return updateGame(id, { description });
};

// Not güncelle
export const updateGameNotes = async (id: string, notes: string): Promise<boolean> => {
  return updateGame(id, { notes });
};
